
import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '@/data/blogPosts';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((post) => post.slug === slug);
  
  useEffect(() => {
    // Scroll to top when navigating to a blog post
    window.scrollTo(0, 0);
  }, [slug]);
  
  if (!post) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">Post Not Found</h2>
        <p>The blog post you're looking for doesn't exist or has been removed.</p>
        <Button asChild className="mt-6">
          <Link to="/blog">Back to Blog</Link>
        </Button>
      </div>
    );
  }
  
  // Extract related calculators based on keywords in the post content
  const relatedTools = getRelatedTools(post.content, post.category);
  
  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-8">
        <Link to="/blog" className="text-blue-600 hover:text-blue-800">
          ← Back to Blog
        </Link>
      </div>
      
      {post.image && (
        <div className="mb-8">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-64 object-cover rounded-lg shadow-md"
          />
        </div>
      )}
      
      <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
      
      <div className="flex items-center mb-6 text-gray-600">
        <span>{post.date}</span>
        {post.category && (
          <>
            <span className="mx-2">•</span>
            <Link 
              to={`/blog/category/${post.category.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm"
            >
              {post.category}
            </Link>
          </>
        )}
      </div>
      
      <Separator className="mb-6" />
      
      <div 
        className="prose max-w-none"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />
      
      {relatedTools.length > 0 && (
        <div className="mt-10 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-2">Related Tools</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {relatedTools.map((tool, index) => (
              <li key={index}>
                <Link 
                  to={tool.path} 
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {tool.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Helper function to find related tools based on content and category
function getRelatedTools(content: string, category?: string) {
  const relatedTools: Array<{name: string, path: string}> = [];
  
  // Map categories to relevant tool paths
  if (category === 'Finance') {
    relatedTools.push(
      {name: 'Mortgage Calculator', path: '/special/mortgage'},
      {name: 'Loan Calculator', path: '/special/loan'},
      {name: 'Investment Calculator', path: '/special/investment'}
    );
  } else if (category === 'Health') {
    relatedTools.push(
      {name: 'BMI Calculator', path: '/special/bmi'},
      {name: 'BMR Calculator', path: '/special/bmr'},
      {name: 'Ideal Weight Calculator', path: '/special/ideal-weight'}
    );
  } else if (category === 'Tools') {
    relatedTools.push(
      {name: 'Length Converter', path: '/converters/length'},
      {name: 'Weight Converter', path: '/converters/weight'},
      {name: 'Temperature Converter', path: '/converters/temperature'}
    );
  }
  
  // Look for specific keywords in content
  if (content.includes('mortgage')) {
    if (!relatedTools.some(t => t.path === '/special/mortgage')) {
      relatedTools.push({name: 'Mortgage Calculator', path: '/special/mortgage'});
    }
  }
  
  if (content.includes('BMI') || content.includes('body mass')) {
    if (!relatedTools.some(t => t.path === '/special/bmi')) {
      relatedTools.push({name: 'BMI Calculator', path: '/special/bmi'});
    }
  }
  
  if (content.includes('convert') || content.includes('unit')) {
    if (!relatedTools.some(t => t.path === '/converters/length')) {
      relatedTools.push({name: 'Unit Converters', path: '/converters'});
    }
  }
  
  return relatedTools;
}

export default BlogPost;
